import * as React from 'react';
import * as RadixScrollArea from '@radix-ui/react-scroll-area';
import { FixMeLater, getLanguageDirection } from '../../utils';
import { Styler, StylerBaseProps } from '../Styler';
import { useStyles } from './ScrollArea.styles';
import { useComponentDefaultProps } from '../../theming';

export interface ScrollAreaProps extends StylerBaseProps {
    /**
     * The content of the `ScrollArea`.
     */
    children?: React.ReactNode;

    /**
     * The width of the scrollbar.
     * @default 6
     */
    scrollbarSize?: number | string;

    /**
     * Sets when the scrollbar will appear.
     * @default hover
     */
    scrollbarVisibility?: 'auto' | 'always' | 'scroll' | 'hover' | 'never';

    /**
     * The time in milliseconds before the scrollbar hides.
     * Only relevant if the `displayType` is either `scroll` or `hover`.
     * @default 1000
     */
    scrollbarHideDelay?: number;

    /**
     * If `true`, the horizontal scrollbar will be offset so that it doesn't obscure content.
     * @default false
     */
    offsetScrollbarX?: boolean;

    /**
     * If `true`, the vertical scrollbar will be offset so that it doesn't obscure content.
     * @default false
     */
    offsetScrollbarY?: boolean;

    /**
     * Callback fired when the scroll position changes.
     */
    onScrollPositionChange?(position: { x: number; y: number }): void;

    /**
     * A reference to the ScrollArea viewport.
     */
    viewportRef?: React.ForwardedRef<HTMLDivElement>;

    /**
     * If `true`, negative horizontal margins will be applied so the content abuts the edge of the viewport when overflowing.
     * This should be used when the `ScrollArea` is filling a `container` with standard content margin.
     * @default false
     */
    contained?: boolean;
}

const defaultProps: Partial<ScrollAreaProps> = {
    scrollbarSize: 6,
    scrollbarVisibility: 'hover',
    scrollbarHideDelay: 1000,
    offsetScrollbarX: false,
    offsetScrollbarY: false,
    contained: false,
};

export const ScrollArea: React.ForwardRefExoticComponent<ScrollAreaProps & React.RefAttributes<HTMLElement>> =
    React.forwardRef(function ScrollArea(
        props: ScrollAreaProps,
        ref: React.ForwardedRef<FixMeLater>
    ): React.JSX.Element {
        const {
            children,
            scrollbarSize = 6,
            scrollbarVisibility,
            scrollbarHideDelay,
            offsetScrollbarX = false,
            offsetScrollbarY = false,
            onScrollPositionChange,
            viewportRef,
            contained = false,
            ...other
        } = useComponentDefaultProps('ScrollArea', defaultProps, props);

        const [scrollbarHovered, setScrollbarHovered] = React.useState(false);

        const { styles } = useStyles({
            scrollbarSize,
            offsetScrollbarX,
            offsetScrollbarY,
            scrollbarHovered,
            hidden: scrollbarVisibility === 'never',
            contained,
        });

        const scrollbarProps: Partial<RadixScrollArea.ScrollAreaScrollbarProps> = {
            css: styles.scrollbar,
            forceMount: true,
            onMouseEnter: () => setScrollbarHovered(true),
            onMouseLeave: () => setScrollbarHovered(false),
        };

        return (
            <RadixScrollArea.Root
                type={scrollbarVisibility === 'never' ? 'always' : scrollbarVisibility}
                scrollHideDelay={scrollbarHideDelay}
                dir={getLanguageDirection()}
                ref={ref}
                asChild={true}
            >
                <Styler baseStyles={styles.root} {...other}>
                    <RadixScrollArea.Viewport
                        // {...viewportProps}
                        css={styles.viewport}
                        ref={viewportRef}
                        onScroll={
                            typeof onScrollPositionChange === 'function'
                                ? ({ currentTarget }) =>
                                      onScrollPositionChange({
                                          x: currentTarget.scrollLeft,
                                          y: currentTarget.scrollTop,
                                      })
                                : undefined
                        }
                    >
                        {children}
                    </RadixScrollArea.Viewport>
                    <RadixScrollArea.Scrollbar orientation="horizontal" {...scrollbarProps}>
                        <RadixScrollArea.Thumb css={styles.thumb} />
                    </RadixScrollArea.Scrollbar>
                    <RadixScrollArea.Scrollbar orientation="vertical" {...scrollbarProps}>
                        <RadixScrollArea.Thumb css={styles.thumb} />
                    </RadixScrollArea.Scrollbar>
                    <RadixScrollArea.Corner css={styles.corner} />
                </Styler>
            </RadixScrollArea.Root>
        );
    });
